import { ComponentProps, useMemo, useRef, useState } from "react";
import locale from "@bonliva-crm/locale";
import { MultiSelect, sortSelectedFirst } from "@bonliva-crm/ui";
import { useGlobalStore, useUpdateEffect } from "@hooks";
import { useFormField } from "@clientUtils";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";

export type CanWorkInFilterData = string[];

export type CanWorkInFilterProps = FilterWithFavouriteProps;

export const CanWorkInFilter = (props: CanWorkInFilterProps) => {
  const store = useGlobalStore();
  const title = props.label ?? locale["Can work in"];

  const options = useMemo(() => {
    return store.cache.countries.toLabelValue(locale.getCountryName);
  }, [store.cache.countries]);

  const selectRef = useRef<any>(null);
  const { field, setFieldValue } = useFormField<CanWorkInFilterData>(props.name);
  const [expanded, setExpanded] = useState(field?.value?.length > 0);

  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current?.focus();
    }
  }, [expanded]);

  const commonProps: ComponentProps<typeof MultiSelect<Option>> = {
    options,
    name: props.name,
    value: field?.value?.toLabelValue(locale.getCountryName) || [],
    onChange: (selected) => {
      field.onChange(selected.map((s) => s.value));
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: title,
    isSearchable: true,
    maxMenuHeight: 600,
    showBadge: field?.value !== null,
    optionType: "checkbox",
    isClearable: field?.value !== null,
    sortOptionsOnMenuOpenFn: (options) =>
      sortSelectedFirst({
        options,
        value: field?.value,
        valueOptionEqualityFn: (value, option) => value === option.value
      }),
    closeMenuOnSelect: false,
    hidePlaceholderOnFocus: false,
    showOptionDivider: false
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={field.value?.length > 0}
        isClearable={field.value?.length > 0}
        isExpanded={expanded}
        onClear={() => {
          setFieldValue([]);
          setExpanded(!expanded);
        }}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <MultiSelect<Option>
          {...commonProps}
          selectRef={selectRef}
          isClearable={false}
          openMenuOnFocus
          menuPortalTarget={document.body}
          menuPlacement="auto"
          name={"availableForWorkInCountries"}
          controlShouldRenderValue
          showBadge={false}
          placeholder={locale.SelectValue}
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <MultiSelect<Option>
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
};

type Option = { label: string; value: string | null };
